import * as Sentry from '@sentry/react';
import { type ReactNode } from 'react';
import { ApiError } from '@/services/queryUtils';
import ErrorFallback from '@/components/ErrorFallback';
import NoAccessMessage from './NoAccessMessage';
import type { Network } from '@/types';

interface PageErrorBoundaryProps {
  children: ReactNode;
  networks?: Network[];
}

/**
 * Sentry error boundary shared by the outer `PageBoundary` route wrapper (no networks prop, catches
 * 403s thrown from within `PageWrapper` itself, e.g. `useUserNetworksQuery`) and the inner Outlet
 * slot inside `PageWrapper` (networks in scope, catches 403s from page-level queries).
 * Maps 403 ApiErrors to NoAccessMessage; all other errors to ErrorFallback.
 */
const PageErrorBoundary = ({ children, networks }: PageErrorBoundaryProps) => (
  <Sentry.ErrorBoundary
    showDialog={false}
    beforeCapture={(scope, _error, componentStack) => {
      scope.setExtra('componentStack', componentStack);
    }}
    fallback={({ error }) =>
      error instanceof ApiError && error.response?.status === 403 ? (
        <NoAccessMessage hasNetworks={Boolean(networks?.length)} />
      ) : (
        <ErrorFallback />
      )
    }
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default PageErrorBoundary;
